// A template on a placed monster (issue 1054): the DM adds a goblin as a Half-fiend Goblin and the token's numbers
// change. Pure, so the "Place" dialog's preview and the server's placing compute the same numbers: the template's
// ability adjustments, natural armour bonus and CR adjustment, applied to the monster's HP, AC and CR. A Dex change
// moves AC by the change in its modifier; a Con change moves HP by the change in its modifier times the Hit Dice;
// natural armour adds to AC. HP never falls below 1. A number that cannot be read is left as it was, and the list of
// changes says so in words. Prose-only changes (special attacks and qualities) are not applied here.
/**
 * The registers a monster's numbers above are read through (issue 1308), whatever the system calls the attributes
 * that tag them: `hp` and `ac` as for any token, and these four. A system whose monsters tag none of them gives a
 * template nothing to change but what it names itself.
 */
export const MONSTER_NUMBER_REGISTERS = { hitDice: 'hit_dice', dex: 'dexterity', con: 'constitution', cr: 'challenge_rating' };
export const TEMPLATE_ADJUST_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha', 'natural_armor_bonus', 'cr_adjustment'];
const ADJUST_MAX = 100;
/** A template's numeric fields out of anything (a site row): finite integers within ±100, the rest dropped. */
export function templateAdjustFrom(o) {
    const out = {};
    if (typeof o !== 'object' || o === null)
        return out;
    for (const k of TEMPLATE_ADJUST_KEYS) {
        const v = o[k];
        if (typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= ADJUST_MAX)
            out[k] = v;
    }
    return out;
}
/** How many Hit Dice: every "NdM" summed ("1d8 plus 3d10+3" is 4), "1/2 d8" is 1/2, "6 HD" is 6; else null. */
export function hitDiceCount(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) && v > 0 && v <= 1000 ? v : null;
    if (typeof v !== 'string' || v.length > 200)
        return null;
    const frac = v.match(/^\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*d\s*\d+/i);
    if (frac) {
        const n = Number(frac[1]) / Number(frac[2]);
        return n > 0 && n < 1 ? n : null;
    }
    let total = 0;
    for (const m of v.matchAll(/(\d{1,4})\s*d\s*\d+/gi))
        total += Number(m[1]);
    if (total === 0) {
        const hd = v.match(/^\s*(\d{1,4})\s*HD\b/i);
        if (hd)
            total = Number(hd[1]);
    }
    return total > 0 && total <= 1000 ? total : null;
}
const FRACTIONS = [[1 / 8, '1/8'], [1 / 6, '1/6'], [1 / 4, '1/4'], [1 / 3, '1/3'], [1 / 2, '1/2']];
/** A challenge rating: 3, "3", "1/3", "0.5"; null for anything else. */
export function parseCr(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) && v > 0 && v <= 100 ? v : null;
    if (typeof v !== 'string')
        return null;
    const f = v.match(/^\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*$/);
    if (f) {
        const n = Number(f[1]) / Number(f[2]);
        return n > 0 && n <= 100 ? n : null;
    }
    const d = v.match(/^\s*(\d{1,3}(?:\.\d+)?)\s*$/);
    if (d) {
        const n = Number(d[1]);
        return n > 0 && n <= 100 ? n : null;
    }
    return null;
}
/** A challenge rating as written: "1/3", "2", "0.1". */
export function formatCr(n) {
    const f = FRACTIONS.find(([v]) => Math.abs(v - n) < 0.001);
    if (f)
        return f[1];
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
/** The ability modifier of a score (3.5 and Pathfinder alike). */
export const abilityModifier = (score) => Math.floor((score - 10) / 2);
const signed = (n) => (n >= 0 ? `+${n}` : `-${Math.abs(n)}`);
const LABELS = { str: 'Str', int: 'Int', wis: 'Wis', cha: 'Cha' };
const intOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null);
/**
 * The monster's numbers with the template applied, and the changes in words. CR: a whole adjustment on a fractional
 * CR counts from nothing (a CR 1/3 goblin + 2 is CR 2, as the books treat a CR below 1 raised a whole step); a
 * negative one never takes a CR of 1 or more below 1, and leaves a fractional one as it was.
 */
export function applyTemplate(m, t) {
    const changes = [];
    const hp0 = intOrNull(m.hp);
    const ac0 = intOrNull(m.ac);
    const dex0 = intOrNull(m.dex);
    const con0 = intOrNull(m.con);
    const cr0 = parseCr(m.cr);
    const hd = hitDiceCount(m.hitDice);
    let acDelta = 0;
    let hpDelta = 0;
    let dex = dex0;
    let con = con0;
    const dexAdj = t.dex ?? 0;
    if (dexAdj !== 0) {
        if (dex0 === null)
            changes.push(`Dex ${signed(dexAdj)}: the monster's Dex can't be read, so its AC is left as it was`);
        else {
            dex = dex0 + dexAdj;
            const d = abilityModifier(dex) - abilityModifier(dex0);
            acDelta += d;
            changes.push(`Dex ${dex0} → ${dex} (${d === 0 ? 'AC unchanged' : `${signed(d)} AC`})`);
        }
    }
    const conAdj = t.con ?? 0;
    if (conAdj !== 0) {
        if (con0 === null)
            changes.push(`Con ${signed(conAdj)}: the monster has no Con that can be read, so its HP is left as it was`);
        else {
            con = con0 + conAdj;
            const d = abilityModifier(con) - abilityModifier(con0);
            if (d === 0)
                changes.push(`Con ${con0} → ${con} (HP unchanged)`);
            else if (hd === null)
                changes.push(`Con ${con0} → ${con}: the Hit Dice can't be read, so its HP is left as it was`);
            else {
                hpDelta = Math.trunc(d * hd);
                changes.push(`Con ${con0} → ${con} (${signed(hpDelta)} HP${hd === 1 ? '' : `: ${signed(d)} × ${hd < 1 ? formatCr(hd) : hd} HD`})`);
            }
        }
    }
    const nat = t.natural_armor_bonus ?? 0;
    if (nat !== 0) {
        acDelta += nat;
        changes.push(`Natural armor ${signed(nat)} AC`);
    }
    let ac = ac0;
    if (acDelta !== 0) {
        if (ac0 === null)
            changes.push("The monster's AC can't be read, so it is left as it was");
        else
            ac = ac0 + acDelta;
    }
    let hp = hp0;
    if (hp0 !== null && hpDelta !== 0) {
        hp = hp0 + hpDelta;
        if (hp < 1) {
            hp = 1;
            changes.push('HP stays at 1: it never falls below');
        }
    }
    else if (hp0 === null && hpDelta !== 0)
        changes.push("The monster's HP can't be read, so it is left as it was");
    let cr = cr0;
    const crAdj = t.cr_adjustment ?? 0;
    if (crAdj !== 0) {
        if (cr0 === null)
            changes.push(`CR ${signed(crAdj)}: the monster's CR can't be read, so it is left as it was`);
        else {
            if (crAdj > 0)
                cr = cr0 < 1 ? crAdj : cr0 + crAdj;
            else
                cr = cr0 < 1 ? cr0 : Math.max(1, cr0 + crAdj);
            changes.push(`CR ${formatCr(cr0)} → ${formatCr(cr)}`);
        }
    }
    const others = ['str', 'int', 'wis', 'cha'].filter((k) => (t[k] ?? 0) !== 0).map((k) => `${LABELS[k]} ${signed(t[k])}`);
    if (others.length)
        changes.push(`${others.join(', ')} (not on the token)`);
    if (!changes.length)
        changes.push('No change to the numbers');
    return { hp, ac, cr, dex, con, changes };
}
/** The placed token's name: "<Template> <Monster>", cut to the token's 120 characters. */
export const templatedName = (template, monster) => `${template.trim()} ${monster.trim()}`.trim().slice(0, 120);
//# sourceMappingURL=template.js.map